// Css
import './TipTapMentionSuggestion.css';

// React imports
import React, { useState, forwardRef, useEffect, useImperativeHandle } from 'react'
import { ReactRenderer } from '@tiptap/react'
import { computePosition, autoUpdate, offset } from '@floating-ui/dom'

// Generic Import
import _ from 'lodash';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers } from '@fortawesome/free-solid-svg-icons';

// Build the suggestion items list (users + teams) from an organization. Users are
// keyed by email; teams are keyed by `team:<teamUniqueId>`. Server-side notification
// dispatch detects the `team:` prefix and fans out to that team's members.
function mentionItemsFromOrg({ organization }) {
    const users = (organization?.users || []).map(u => ({
        id: u.email,
        label: u.email,
        type: 'user',
    }));
    const teamSet = new Set();
    const teams = [];
    for (const u of (organization?.users || [])) {
        if (!u.team) continue;
        const [name, uniqueId] = u.team.split(':');
        if (!name || !uniqueId) continue;
        const key = `${name}:${uniqueId}`;
        if (teamSet.has(key)) continue;
        teamSet.add(key);
        teams.push({
            id: `team:${key}`,
            label: name,
            sublabel: 'Team',
            type: 'team',
        });
    }
    return [...teams, ...users];
}

// Accepts items: [{ id, label, type, sublabel? }]. For backwards compatibility the
// caller may pass `emails` (string[]) and the suggestion will treat them as users.
const MentionSuggestion = ({ items, emails }) => {
    const _items = items || (emails || []).map(e => ({ id: e, label: e, type: 'user' }));
    return {
        items: ({ query }) => {
            const q = (query || '').toLowerCase();
            return _items
                .filter(item => (item.label || '').toLowerCase().includes(q) || (item.id || '').toLowerCase().includes(q))
                .slice(0, 8);
        },

        render: () => {
            let component
            let popup
            let cleanup

            return {
                onStart: props => {
                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    })

                    if (!props.clientRect) {
                        return
                    }

                    // Create popup element
                    popup = document.createElement('div')
                    popup.style.position = 'absolute'
                    popup.style.zIndex = '1000000000'
                    popup.appendChild(component.element)
                    document.body.appendChild(popup)

                    // Setup Floating UI positioning
                    const updatePosition = () => {
                        if (!props.clientRect) return
                        
                        const rect = props.clientRect()
                        computePosition(
                            {
                                getBoundingClientRect: () => rect,
                            },
                            popup,
                            {
                                placement: 'bottom-start',
                                middleware: [offset(5)],
                            }
                        ).then(({ x, y }) => {
                            popup.style.left = `${x}px`
                            popup.style.top = `${y}px`
                        })
                    }

                    updatePosition()

                    // Auto-update position
                    cleanup = autoUpdate(
                        {
                            getBoundingClientRect: () => props.clientRect(),
                        },
                        popup,
                        updatePosition
                    )
                },

                onUpdate(props) {
                    component.updateProps(props)

                    if (!props.clientRect || !popup) {
                        return
                    }

                    const rect = props.clientRect()
                    computePosition(
                        {
                            getBoundingClientRect: () => rect,
                        },
                        popup,
                        {
                            placement: 'bottom-start',
                            middleware: [offset(5)],
                        }
                    ).then(({ x, y }) => {
                        popup.style.left = `${x}px`
                        popup.style.top = `${y}px`
                    })
                },

                onKeyDown(props) {
                    if (props.event.key === 'Escape') {
                        if (popup && popup.parentNode) {
                            popup.parentNode.removeChild(popup)
                        }
                        if (cleanup) {
                            cleanup()
                        }

                        return true
                    }

                    return component.ref?.onKeyDown(props)
                },

                onExit() {
                    if (popup && popup.parentNode) {
                        popup.parentNode.removeChild(popup)
                    }
                    if (cleanup) {
                        cleanup()
                    }
                    component.destroy()
                },
            }
        },
    }
}

const MentionList = forwardRef((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = index => {
        const item = props.items[index]

        if (item) {
            props.command({ id: item.id, label: item.label })
        }
    }

    const upHandler = () => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
    }

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length)
    }

    const enterHandler = () => {
        selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [props.items])

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === 'ArrowUp') {
                upHandler()
                return true
            }

            if (event.key === 'ArrowDown') {
                downHandler()
                return true
            }

            if (event.key === 'Enter') {
                enterHandler()
                return true
            }

            return false
        },
    }))

    return (
        <div style={{
            background: 'white',
            border: `1px solid rgba(61, 37, 20, .05)`,
            borderRadius: '0.7rem',
            boxShadow: '0px 12px 33px 0px rgba(0, 0, 0, .06), 0px 3.618px 9.949px 0px rgba(0, 0, 0, .04)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.1rem',
            overflow: 'auto',
            padding: '0.4rem',
            position: 'relative',
            zIndex: 1000,
        }}>
            {props.items.length
                ? props.items.map((item, index) => (
                    <button
                        className={index === selectedIndex ? 'tiptap-mention-suggestion-button is-selected' : 'tiptap-mention-suggestion-button'}
                        key={`${item.type || 'user'}:${item.id || index}`}
                        onClick={() => selectItem(index)}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    >
                        <FontAwesomeIcon icon={item.type === 'team' ? faUsers : faUser} style={{ width: '14px', color: item.type === 'team' ? '#7c3aed' : '#64748b' }} />
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {item.sublabel && (
                            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{item.sublabel}</span>
                        )}
                    </button>
                ))
                : <div className="item">No result</div>
            }
        </div>
    )
})

export default MentionSuggestion;
export { mentionItemsFromOrg };